import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { auth, db } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';

/**
 * The employee's leave history: their own requests (LeaveRequests) and the
 * leaves an admin entered for them (EmployeeLeaves, always approved), newest
 * first. Loaded when one of the leave tabs is open.
 */
const useLeaveRequests = ({ employeeId, userData, active }) => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [myLeaveRequests, setMyLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchMyLeaveRequests = useCallback(async () => {
    const currentEmployeeId = employeeId || auth.currentUser?.uid;
    if (!currentEmployeeId) return;

    setLoading(true);
    try {
      const requestSnapshot = await getDocs(query(collection(db, 'LeaveRequests'), where('employeeId', '==', currentEmployeeId)));
      const leaveRequests = requestSnapshot.docs.map((d) => ({ id: d.id, ...d.data(), source: 'LeaveRequests' }));

      const leaveSnapshot = await getDocs(query(collection(db, 'EmployeeLeaves'), where('employeeId', '==', currentEmployeeId)));
      const employeeLeaves = leaveSnapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          employeeId: data.employeeId,
          employeeName: `${userData.eName} ${userData.eSurname}`,
          employeeEmail: userData.eMail,
          leaveType: data.typeKey,
          startDate: data.startDate,
          endDate: data.workReturnDate,
          leaveDays: data.leaveDays,
          travelDays: data.travelDays,
          reason: data.reason,
          address: data.address,
          status: 'APPROVED', // Admin tarafından oluşturulan izinler otomatik onaylı
          createdAt: data.createdAt || new Date(),
          processedAt: data.createdAt || new Date(),
          processedBy: 'Admin',
          adminNote: 'Admin tarafından oluşturuldu',
          source: 'EmployeeLeaves',
        };
      });

      const allRequests = [...leaveRequests, ...employeeLeaves].sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });
      setMyLeaveRequests(allRequests);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      toast.error(tRef.current('profilePage.leave.toast.loadError'));
    } finally {
      setLoading(false);
    }
  }, [employeeId, userData.eName, userData.eSurname, userData.eMail]);

  useEffect(() => {
    if (active) fetchMyLeaveRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return { myLeaveRequests, loading, fetchMyLeaveRequests };
};

export default useLeaveRequests;
